import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "./contexts/AuthContext";
import { CelebrationProvider } from "./contexts/CelebrationContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useSessionManager } from "./hooks/useSessionManager";
import { GlobalKeyboardShortcuts } from "./components/GlobalKeyboardShortcuts";
import { AppLayout } from "./components/AppLayout";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Lazy load routes for code splitting
const Home = lazy(() => import("./pages/Home"));
const Progress = lazy(() => import("./pages/Progress"));
const Phases = lazy(() => import("./pages/Phases"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Settings = lazy(() => import("./pages/Settings"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function SessionManager({ children }: { children: React.ReactNode }) {
  useSessionManager();
  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <CelebrationProvider>
                <SessionManager>
                  <GlobalKeyboardShortcuts />
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/auth" replace />} />
                      
                      {/* Protected Routes */}
                      <Route path="/home" element={
                        <ProtectedRoute>
                          <AppLayout><Home /></AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/progress" element={
                        <ProtectedRoute>
                          <AppLayout><Progress /></AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/phases" element={
                        <ProtectedRoute>
                          <AppLayout><Phases /></AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/achievements" element={
                        <ProtectedRoute>
                          <AppLayout><Achievements /></AppLayout>
                        </ProtectedRoute>
                      } />
                      <Route path="/settings" element={
                        <ProtectedRoute>
                          <AppLayout><Settings /></AppLayout>
                        </ProtectedRoute>
                      } />
                      
                      {/* Public/Auth Routes */}
                      <Route path="/auth" element={<Auth />} />
                      
                      {/* Admin Route */}
                      <Route 
                        path="/admin" 
                        element={
                          <ProtectedRoute requireAdmin>
                            <Admin />
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </SessionManager>
              </CelebrationProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;